/**
 * What the native app's shell does with a top-level navigation, for the binaries that
 * predate the bridge (`window.HeruApp`, `OPEN_EXTERNAL`): everything up to 1.4.0, which
 * is what both stores publish today. Mirrors `handleShouldStartLoadWithRequest` of
 * heru-mobile-app at commit `bafee0c` (`app/index.tsx`), in this order:
 *
 *  1. The path ends in a document extension (`LEGACY_DOWNLOAD_EXTENSIONS`): the shell
 *     opens the URL in the system browser and the WebView stays where it is.
 *  2. The host is one the shell allows (`LEGACY_WEBVIEW_HOSTS`): the URL loads INSIDE the
 *     WebView, replacing the app screen with the raw file and no way back.
 *  3. Any other host: the system browser.
 *
 * So `window.location.assign(url)` is a safe hand-off to the system browser only when
 * `opensInLegacySystemBrowser(url)` is true. Keep the lists below equal to the shell's;
 * binaries with the bridge do not need any of this. `toLegacyBrowserUrl` widens that
 * rule to our own signed links (see `legacyDownloadLink.ts`).
 */

/** Document extensions the 1.4.0 shell sends to the system browser (`isDownloadableFile`). */
const LEGACY_DOWNLOAD_EXTENSIONS = /\.(pdf|csv|xlsx?|docx?|pptx?|zip|xml|txt|cer|key|p12|pem)(\?|#|$)/i;

/**
 * Hosts the 1.4.0 shell loads inside the WebView (`ALLOWED_HOST_PATTERNS`): heru.app and
 * its subdomains (`web.heru.app`, `apis.heru.app`) plus the storage, CDN and payment hosts.
 */
const LEGACY_WEBVIEW_HOSTS: RegExp[] = [
  /(^|\.)heru\.app$/,
  /(^|\.)cloudfront\.net$/,
  /(^|\.)googleapis\.com$/,
  /(^|\.)googleusercontent\.com$/,
  /(^|\.)amazonaws\.com$/,
  /(^|\.)stripe\.com$/,
  /(^|\.)stripe\.network$/,
];

/** Schemes the shell always lets the WebView handle itself. */
const LEGACY_WEBVIEW_SCHEMES = ['about:', 'data:', 'blob:', 'file:'];

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

/** True when the path (not the query or hash) ends in an extension the 1.4.0 shell downloads. */
export const hasLegacyDownloadExtension = (url: string): boolean => {
  const parsed = parseUrl(url);
  return LEGACY_DOWNLOAD_EXTENSIONS.test(parsed ? parsed.pathname : url);
};

/**
 * Whether a binary without the bridge sends a top-level navigation to `url` to the
 * system browser (true) instead of loading it in the WebView (false).
 */
export const opensInLegacySystemBrowser = (url: string): boolean => {
  if (hasLegacyDownloadExtension(url)) return true;
  if (LEGACY_WEBVIEW_SCHEMES.some((scheme) => url.startsWith(scheme))) return false;
  const parsed = parseUrl(url);
  if (!parsed) return false;
  return !LEGACY_WEBVIEW_HOSTS.some((host) => host.test(parsed.hostname));
};
